import type { CalendarItem, CalendarItemKind } from '../../api/organizer';

/** How many chips a month cell shows before collapsing into "+N more". */
export const CHIPS_PER_CELL = 3;

/**
 * Items keyed by the day they appear on.
 *
 * Each item is fanned out over its own `days` array, which the API precomputed —
 * so a three-day all-day meeting appears in three cells and the component does no
 * date maths at all.
 */
export function groupByDay(items: CalendarItem[]): Map<string, CalendarItem[]> {
  const byDay = new Map<string, CalendarItem[]>();
  for (const item of items) {
    for (const day of item.days) {
      const bucket = byDay.get(day);
      if (bucket) bucket.push(item);
      else byDay.set(day, [item]);
    }
  }
  return byDay;
}

/** Tailwind classes for a chip, so each kind reads differently at a glance. */
export function chipClasses(item: CalendarItem): string {
  if (item.kind === 'meeting') {
    const meta = item.meta.kind === 'meeting' ? item.meta : null;
    const base = 'border-l-2 border-accent bg-surface-secondary text-text-secondary';
    // Unanswered invitations are dashed; anything showing as free is dimmed.
    const tentative =
      meta?.responseStatus === 'needsAction' || meta?.responseStatus === 'tentative'
        ? ' border-dashed'
        : '';
    const free = meta && !meta.busy ? ' opacity-60' : '';
    return base + tentative + free;
  }

  if (item.kind === 'reminder') {
    return 'border-l-2 border-warning bg-warning-light/40 text-text-secondary';
  }

  const meta = item.meta.kind === 'task' ? item.meta : null;
  if (meta?.overdue) return 'border-l-2 border-error bg-error-light text-error-dark';
  if (meta?.status === 'done') return 'border-l-2 border-border bg-surface-secondary text-text-muted line-through';
  return 'border-l-2 border-text-primary bg-surface-secondary text-text-secondary';
}

const KIND_GLYPH: Record<CalendarItemKind, string> = {
  meeting: '',
  reminder: '⏰',
  task: '•',
};

/** The one-line label for a chip. */
export function chipLabel(item: CalendarItem): string {
  const glyph = KIND_GLYPH[item.kind];
  const time = item.startTimeLabel ? `${item.startTimeLabel} ` : '';
  return `${glyph ? `${glyph} ` : ''}${time}${item.title}`.trim();
}

/** The task a chip points at, when it points at one. */
export function taskIdOf(item: CalendarItem): string | null {
  if (item.meta.kind === 'task' || item.meta.kind === 'reminder') return item.meta.taskId;
  return null;
}
